import { Constants, _adapterRegistry } from '../shared/Constants';
import { AjaxBuilder } from './AjaxBuilder';
import { AjaxResponder } from './AjaxResponder';
import { ActionBuilder } from './ActionBuilder';
import { ToolbarBuilder } from './ToolbarBuilder';
import { DataTableAdapter } from '../adapter/DataTableAdapter';
import { ColumnBuilder } from './ColumnBuilder';
import { SummaryBuilder as InternalSummaryBuilder } from './SummaryBuilder';
import { normalizeSummaryConfig as internalNormalizeSummaryConfig } from '../features/summary/normalizeSummaryConfig';
import { buildDomLayout } from '../features/layout/buildDomLayout';
import { applyFixedWidthFixes } from '../features/layout/fixedWidthFixes';
import { attachFilterPlucking, debounce } from '../features/layout/filterPlucking';
import {
    diagnose as runDiagnostics,
    registerDiagnostic as registerDiag,
    unregisterDiagnostic as unregisterDiag,
    getDiagnostics as getAllDiags
} from '../diagnostics/index';

/**
 * HeaderGroupBuilder Class
 */
export class HeaderGroupBuilder {
    constructor(title) {
        this.title = title;
        this.columns = [];
        this.actionCallbacks = {};
    }

    withColumn(builderFn) {
        const columnBuilder = new ColumnBuilder();
        builderFn(columnBuilder);
        const built = columnBuilder.build();
        this.columns.push(built);

        if (built && built._actionCallbacks && typeof built._actionCallbacks === 'object') {
            Object.assign(this.actionCallbacks, built._actionCallbacks);
        }
        return this;
    }
}

/**
 * DataTableBuilder Class
 * @template T
 */
export class DataTableBuilder {
    constructor(selector) {
        this._selector = selector;
        /** @type {any} */
        this._config = {
            columns: [],
            ajax: null,
            paging: true,
            searching: true,
            responsive: false,
            serverSide: false,
            processing: true,
            order: [[0, 'asc']],
            select: false,
            buttons: [],
            fixedColumns: null,
            autoWidth: false,
            info: true,
            lengthChange: true,
            dom: null, // Built dynamically
            theme: null, // Custom theme class
            compactPaging: false,
            denseRows: false,
            language: {
                processing: '<div class="dt-loader-wrapper"><div class="dt-loader-text">Processing...</div></div>',
                search: "_INPUT_",
                searchPlaceholder: "Search records...",
                lengthMenu: "_MENU_",
                info: "Showing _START_ to _END_ of _TOTAL_ entries",
                infoEmpty: "Showing 0 to 0 of 0 entries",
                infoFiltered: "(filtered from _MAX_ total entries)",
                paginate: {
                    first: '<i class="bi bi-chevron-double-left"></i>',
                    previous: '<i class="bi bi-chevron-left"></i>',
                    next: '<i class="bi bi-chevron-right"></i>',
                    last: '<i class="bi bi-chevron-double-right"></i>'
                }
            }
        };
        this._jumpToPage = false;
        this._explicitOrder = false;
        this._actionCallbacks = {};
        this._adapter = null;
        this._filters = null;
        this._searchTriggers = [];
        this._deferLoad = false;
        this._headerTree = [];
    }

    static Trigger = Constants.Events;

    /**
     * Runs plugin-based diagnostics on DataTable layout, styling, and regression checks.
     * @param {Object|string|HTMLElement} [optionsOrTarget]
     * @returns {Promise<import('../diagnostics/DiagnosticEngine').DiagnosticReport>}
     */
    static diagnose(optionsOrTarget) {
        return runDiagnostics(optionsOrTarget);
    }

    /**
     * Registers a diagnostic plugin.
     * @param {import('../diagnostics/DiagnosticRegistry').DiagnosticPlugin} plugin
     */
    static registerDiagnostic(plugin) {
        return registerDiag(plugin);
    }

    /**
     * Unregisters a diagnostic plugin by ID.
     * @param {string} id
     */
    static unregisterDiagnostic(id) {
        return unregisterDiag(id);
    }

    /**
     * Returns all registered diagnostic plugins.
     * @returns {import('../diagnostics/DiagnosticRegistry').DiagnosticPlugin[]}
     */
    static getDiagnostics() {
        return getAllDiags();
    }

    /**
     * @param {string} selector
     * @returns {DataTableBuilder<{}>}
     */
    static create(selector) {
        return new DataTableBuilder(selector);
    }

    /**
     * Merges a raw DataTables options object directly into the config.
     * Escape hatch for callers that pass plain DataTables.net options instead of using the fluent builders.
     */
    withOptions(options = {}) {
        this._config = { ...this._config, ...options };
        if (Object.prototype.hasOwnProperty.call(options, 'order')) {
            this._explicitOrder = true;
        }
        return this;
    }

    withAjax(urlOrFn, data = null, type = Constants.Http.GET) {
        // Fluent AjaxBuilder form: withAjax(ajax => ajax.withUrl(...))
        // This works for both client-side and server-side mode because DataTables accepts
        // the same ajax config object shape in either case.
        if (typeof urlOrFn === 'function' && urlOrFn.length === 1) {
            const ajaxBuilder = new AjaxBuilder();
            urlOrFn(ajaxBuilder);
            this._config.ajax = ajaxBuilder._build();
            return this;
        }
        if (typeof urlOrFn === 'function') {
            this._config.ajax = (data2, callback, settings) => {
            const responder = new AjaxResponder(data2, callback);
            urlOrFn(data2, responder, settings);
            };
            return this;
        }
        if (typeof urlOrFn === 'object' && urlOrFn !== null) {
            const config = urlOrFn;
            urlOrFn = config.url;
            type = config.type || type;
            data = config.data || data;
        }
        const validMethods = Object.values(Constants.Http);
        if (!validMethods.includes(type)) {
            console.warn(`DataTableBuilder: Invalid HTTP method '${type}'. Defaulting to GET. Valid: ${validMethods.join(", ")}`);
            type = Constants.Http.GET;
        }
        this._config.ajax = {
            url: urlOrFn,
            data,
            type
        };
        return this;
    }

    withPaging(enabledOrLength = 10) {
        if (enabledOrLength === false) {
            this._config.paging = false;
        } else {
            this._config.paging = true;
            const length = typeof enabledOrLength === 'number' ? enabledOrLength : 10;
            this._config.pageLength = length;

            // Sync lengthMenu to include the current pageLength if it's missing
            if (!this._config.lengthMenu) {
                this._config.lengthMenu = [5, 10, 25, 50, 100];
            }

            if (Array.isArray(this._config.lengthMenu)) {
                // Handle both 1D [10, 25] and 2D [[10, 25], [10, 25]] formats
                const values = Array.isArray(this._config.lengthMenu[0]) ? this._config.lengthMenu[0] : this._config.lengthMenu;
                if (!values.includes(length)) {
                    if (Array.isArray(this._config.lengthMenu[0])) {
                        this._config.lengthMenu[0].push(length);
                        this._config.lengthMenu[0].sort((a, b) => a - b);
                        this._config.lengthMenu[1].push(length); // Labels usually match values or are strings
                        this._config.lengthMenu[1].sort((a, b) => (typeof a === 'number' && typeof b === 'number') ? a - b : 0);
                    } else {
                        this._config.lengthMenu.push(length);
                        this._config.lengthMenu.sort((a, b) => a - b);
                    }
                }
            }
        }
        return this;
    }

    withSearching(enabled = true) {
        this._config.searching = enabled;
        return this;
    }

    withInfo(enabled = true) {
        this._config.info = enabled;
        return this;
    }

    withLengthMenu(enabledOrMenu = true) {
        if (Array.isArray(enabledOrMenu)) {
            this._config.lengthChange = true;
            this._config.lengthMenu = enabledOrMenu;
        } else {
            this._config.lengthChange = !!enabledOrMenu;
        }
        return this;
    }

    /**
     * Sets the options for the "Entries per page" dropdown.
     * @param {Array} options - Array of numbers, or a 2D array [[values], [labels]]. 
     *                          Supports 'All' or -1 for showing all records.
     * @example .withLengthMenuOptions([10, 25, 50, 'All'])
     */
    withLengthMenuOptions(options) {
        if (!Array.isArray(options)) return this;

        // Convert simple array like [10, 20, 'All'] to DataTables 2D format [[10, 20, -1], [10, 20, 'All']]
        if (options.length > 0 && !Array.isArray(options[0])) {
            const values = options.map(o => (o === 'All' || o === -1) ? -1 : parseInt(o));
            const labels = options.map(o => (o === 'All' || o === -1) ? 'All' : o);
            this._config.lengthMenu = [values, labels];
        } else {
            this._config.lengthMenu = options;
        }
        
        this._config.lengthChange = true;
        return this;
    }

    withServerSide(enabled = true) {
        this._config.serverSide = enabled;
        return this;
    }

    withResponsive(enabled = true) {
        this._config.responsive = enabled;
        return this;
    }

    withScrolling(height = '400px') {
        this._config.scrollY = height;
        this._config.scrollX = true; // Required for reliable header alignment in many layouts
        this._config.scrollCollapse = true;
        return this;
    }

    /**
     * Enables a "Jump to Page" input in the pagination controls.
     * @param {boolean} [enabled=true]
     * @returns {this}
     */
    withJumpToPage(enabled = true) {
        this._jumpToPage = enabled;
        return this;
    }

    /**
     * Joins pagination buttons into a compact segmented control.
     * @param {boolean} [enabled=true]
     * @returns {this}
     */
    withCompactButtons(enabled = true) {
        this._config.compactPaging = enabled;
        return this;
    }

    /**
     * Reduces row height by tightening cell padding for a dense layout.
     * @param {boolean} [enabled=true]
     * @returns {this}
     */
    withDenseRows(enabled = true) {
        this._config.denseRows = enabled;
        return this;
    }

    withDeferRender(enabled = true) {
        this._config.deferRender = enabled;
        return this;
    }

    /**
     * Defers the initial AJAX call until adapter.loadData() is explicitly called.
     * Useful when you need to configure the table UI first and load data on demand.
     * @param {boolean} [enabled=true]
     * @returns {this}
     */
    withDeferLoad(enabled = true) {
        this._deferLoad = enabled;
        return this;
    }
    
    withTheme(theme) {
        this._config.theme = theme;
        return this;
    }

    /**
     * Binds business keys to DOM selectors for automatic AJAX data plucking.
     * @param {Object.<string, string>} filterMap - { PropertyName: '#Selector' }
     * @example .withFilters({ Name: '#txt_Name', Status: '#list_Status' })
     */
    withFilters(filterMap) {
        this._filters = filterMap;
        return this;
    }

    /**
     * Binds one or more events on a selector to reload the table.
     * @param {string} selector - jQuery selector for the trigger
     * @param {string} [events='click'] - Space-separated list of events
     * @returns {this}
     */
    withSearchTrigger(selector, events = Constants.Events.CLICK) {
        this._searchTriggers.push({ selector, events });
        return this;
    }

    /**
     * @deprecated Use .withSearchTrigger() instead.
     */
    autoReloadOn(selector) {
        return this.withSearchTrigger(selector, Constants.Events.CLICK);
    }

    /**
     * @deprecated Seamless is now the default. Use .withCardLayout() to restore the card shell.
     */
    withSeamlessLayout(_enabled = true) {
        console.warn('DataTableBuilder.withSeamlessLayout() is deprecated — seamless is now the default. Use .withCardLayout() for the card shell look.');
        return this;
    }

    /**
     * Restores the card shell (background, border, shadow) around the table.
     * The default is seamless (transparent background, no border/shadow).
     * @param {boolean} [enabled=true]
     * @returns {DataTableBuilder}
     */
    withCardLayout(enabled = true) {
        if (enabled) {
            this._config.cardLayout = true;
        } else {
            this._config.cardLayout = false;
        }
        return this;
    }

    withSelection(mode = Constants.Selection.SINGLE) {
        if (mode === Constants.Selection.NONE) {
            this._config.select = false;
            this._selectionMode = Constants.Selection.NONE;
        } else {
            this._selectionMode = mode;
            // Only pass `select` config to DataTables if the Select extension is loaded.
            // If it isn't, DataTableAdapter handles selection manually via DOM + row tracking.
            if ($.fn.DataTable && $.fn.DataTable.select) {
                this._config.select = {
                    style: mode,
                    selector: 'td.select-checkbox:not(.pe-none)'
                };
            } else {
                // Extension absent — keep select:false so DT doesn't throw.
                // DataTableAdapter manages state internally via _selectedRows Set.
                this._config.select = false;
            }
        }
        return this;
    }

    /**
     * Registers callbacks for selection change events.
     * Works together with .withSelection() to provide custom validation logic.
     * @param {Object} callbacks
     * @param {function(rowData, $tr, adapter): boolean} [callbacks.onSelect] - Called when a row is selected. Return false to reject.
     * @param {function(rowData, $tr, adapter)} [callbacks.onDeselect] - Called when a row is deselected.
     * @param {function(adapter): boolean} [callbacks.onSelectAll] - Called when "select all" is triggered. Return false to reject.
     * @param {function(adapter)} [callbacks.onDeselectAll] - Called when "deselect all" is triggered.
     * @returns {this}
     */
    withSelectionCallbacks(callbacks) {
        this._selectionCallbacks = callbacks || {};
        return this;
    }

    withToolbarButtons(buttonFn) {
        const builder = new ToolbarBuilder();
        
        if (typeof buttonFn === 'function') {
            buttonFn(builder);
            this._config.toolbarButtons = builder._getButtons();
        } else if (Array.isArray(buttonFn)) {
            // Legacy support for array-based configuration
            buttonFn.forEach(btnKey => {
                if (typeof builder[btnKey] === 'function') {
                    builder[btnKey]();
                } else {
                    builder.custom(btnKey);
                }
            });
            this._config.toolbarButtons = builder._getButtons();
        }
        return this;
    }

    withToolbarAlign(align) {
        this._config.toolbarAlign = align || 'center';
        return this;
    }

    withToolbarGap(gap) {
        this._config.toolbarGap = gap;
        return this;
    }

    withFixedColumns(options = { left: 1, right: 0 }) {
        this._config.fixedColumns = options;
        this._config.scrollX = true;
        if (!this._config.scrollY) {
            this._config.scrollY = '400px';
            this._config.scrollCollapse = true;
        }
        // DataTables only forces deterministic column widths for scrollX when
        // autoWidth is on (_fnCalculateColumnWidths is skipped otherwise). Without
        // it, header/body widths are never reconciled and FixedColumns' sticky
        // cells have no stable layout to stick against.
        this._config.autoWidth = true;
        return this;
    }

    withRows(data) {
        this._config.data = data || [];
        return this;
    }

    /**
     * Renders a structured summary block below the table body.
     * Useful for invoice-style totals and side-by-side summary columns.
     * @param {{
     *   scope?: 'all'|'filtered'|'visible'|'page'|'current-page'|'selected',
     *   className?: string,
     *   variant?: string,
     *   emptyText?: string,
     *   gap?: string|number,
     *   valueColumns?: number,
     *   rows: Array<{
     *     label: string,
     *     className?: string,
     *     labelClassName?: string,
     *     cells?: Array<any>,
     *     values?: Array<any>
     *   }>
     * }} summaryConfig
     * @returns {this}
     */
    withSummary(summaryConfigOrBuilderFn) {
        if (typeof summaryConfigOrBuilderFn === 'function') {
            const builder = new InternalSummaryBuilder();
            summaryConfigOrBuilderFn(builder);
            this._config.summary = builder.build();
            return this;
        }

        this._config.summary = internalNormalizeSummaryConfig(summaryConfigOrBuilderFn);
        return this;
    }

    /**
     * @template {string} K
     * @param {function(ColumnBuilder<any>): ColumnBuilder<K>} builderFn
     * @returns {DataTableBuilder<T & Record<K, any>>}
     */
    withColumn(builderFn) {
        const columnBuilder = new ColumnBuilder();
        builderFn(columnBuilder);
        const built = columnBuilder.build();
        this._config.columns.push(built);
        this._headerTree.push({
            type: 'single',
            column: built
        });

        // Allow columns to register delegated action callbacks (e.g. ColumnBuilder.withButton)
        if (built && built._actionCallbacks && typeof built._actionCallbacks === 'object') {
            Object.assign(this._actionCallbacks, built._actionCallbacks);
        }
        return /** @type {any} */ (this);
    }

    /**
     * Creates a multi-level column header group.
     * @param {string} title
     * @param {function(HeaderGroupBuilder): HeaderGroupBuilder} groupFn
     * @returns {DataTableBuilder<any>}
     */
    withHeaderGroup(title, groupFn) {
        const groupBuilder = new HeaderGroupBuilder(title);
        groupFn(groupBuilder);
        const builtCols = groupBuilder.columns;

        this._headerTree.push({
            type: 'group',
            title: title,
            columns: builtCols
        });

        for (const col of builtCols) {
            this._config.columns.push(col);
        }

        if (groupBuilder.actionCallbacks && typeof groupBuilder.actionCallbacks === 'object') {
            Object.assign(this._actionCallbacks, groupBuilder.actionCallbacks);
        }
        return /** @type {any} */ (this);
    }
    /**
     * Registers a callback for the DataTables 'draw' event.
     * @param {function} callback
     * @returns {this}
     */
    onDraw(callback) {
      this._config.drawCallback = callback;
      return this;
    }
    /**
     * Registers a callback for table init and each AJAX data load.
     * Local/`withRows` tables: fires once when initialization completes.
     * AJAX tables: fires after every successful data load (initial, reload, serverSide redraw).
     * @param {function(settings, json, adapter)} callback
     * @returns {this}
     */
    onComplete(callback) {
      this._config.initComplete = callback;
      return this;
    }
    withActions(actionFn, options = {}) {
        const builder = new ActionBuilder();
        actionFn(builder);
        const actions = builder._getActions();
        const header = (typeof builder._getHeader === 'function' && builder._getHeader()) || 'Actions';

        const actionCol = {
            data: null,
            title: header,
            orderable: false,
            searchable: false,
            className: `row-actions ${options.align ? (Constants.Align[String(options.align).toUpperCase()] || options.align) : 'text-center'}`,
            width: '1%', // Shrink-to-fit content, prevents excessive stretching
            render: (data, type, row) => {
                // Filter actions based on showIf predicate
                const visibleActions = actions.filter(act => {
                    if (typeof act.showIf === 'function') {
                        return act.showIf(row);
                    }
                    return true;
                });

                if (visibleActions.length === 0) return '';

                // Integration: Use GenericModal's core action rendering if available
                if (window.GenericModal && typeof window.GenericModal.renderActions === 'function') {
                    const gmActions = visibleActions.map(act => ({
                        key: act.key,
                        icon: act.icon,
                        color: Constants.resolveColor(act.color), 
                        title: act.title || act.key,
                        btnClass: act.className || `gm-btn-${act.key}`
                    }));
                    return window.GenericModal.renderActions(gmActions, { size: 'sm', ...options });
                }

                // Fallback to internal rendering
                return visibleActions.map(act => {
                    const btnClass = act.className || 'btn-outline-secondary';
                    const iconHtml = act.icon
                        ? `<i class="${act.icon}"></i> <span class="dt-btn-label">${act.title || act.key}</span>`
                        : act.key;
                    const title = act.title || act.key;
                    return `<button type="button" class="btn btn-sm ${btnClass}" data-action="${act.key}" title="${title}">${iconHtml}</button>`;
                }).join('');
            }
        };

        this._config.columns.push(actionCol);
        this._headerTree.push({
            type: 'single',
            column: actionCol
        });

        // Register specific action callbacks if defined in builder
        actions.forEach(act => {
            if (act.callback) {
                this._actionCallbacks[act.key] = act.callback;
            }
        });

        return this;
    }

    onAction(key, callback) {
        this._actionCallbacks[key] = callback;
        return this;
    }

    _buildDom() {
        return buildDomLayout(this._config, this._jumpToPage);
    }

    _applyFixedWidthFixes(dtInstance, $el) {
        applyFixedWidthFixes(this._config, dtInstance, $el);
    }

    /**
     * @returns {DataTableAdapter}
     */
    build() {
        const $el = $(this._selector);
        if ($el.length === 0) {
            console.error(`DataTableBuilder: Selector "${this._selector}" not found.`);
            return null;
        }

        // Suppress DataTables' default "Ajax error" alert and handle errors via events.
        // This is global to DataTables; we set it here so consuming pages don't get disruptive popups.
        if ($.fn.DataTable && $.fn.dataTable && $.fn.dataTable.ext) {
            $.fn.dataTable.ext.errMode = 'none';
        }

        const hasHeaderGroups = this._headerTree.some(item => item.type === 'group');

        if (hasHeaderGroups) {
            const $thead = $el.find('thead').length ? $el.find('thead').empty() : $('<thead></thead>').prependTo($el);
            const $topRow = $('<tr></tr>');
            const $bottomRow = $('<tr></tr>');

            let leafColIdx = 0;
            for (const item of this._headerTree) {
                if (item.type === 'single') {
                    const title = item.column.title !== undefined ? item.column.title : '';
                    const $th = $('<th></th>').text(title).attr('rowspan', 2);
                    $topRow.append($th);
                    leafColIdx++;
                } else if (item.type === 'group') {
                    const primaryColIdx = leafColIdx;
                    const isSortable = item.options?.sortable !== false;
                    const $groupTh = $('<th></th>')
                        .text(item.title || '')
                        .attr('colspan', item.columns.length);

                    if (isSortable) {
                        $groupTh
                            .addClass('dt-group-header-sortable sorting')
                            .attr('data-dt-sort-col', primaryColIdx)
                            .css('cursor', 'pointer');
                    }
                    $topRow.append($groupTh);
                    for (const col of item.columns) {
                        const subTitle = col.title !== undefined ? col.title : '';
                        const $subTh = $('<th></th>').text(subTitle);
                        $bottomRow.append($subTh);
                        leafColIdx++;
                    }
                }
            }
            $thead.append($topRow).append($bottomRow);
        } else {
            // Ensure proper table structure for DataTables initialization
            if ($el.find('thead').length === 0) {
                $el.prepend('<thead><tr></tr></thead>');
            } else if ($el.find('thead tr').length === 0) {
                $el.find('thead').append('<tr></tr>');
            }
        }

        // DataTables only forces deterministic column widths (the clone-table
        // measurement pass in _fnCalculateColumnWidths) when autoWidth is on.
        // Per DataTables' own docs, disabling autoWidth without an explicit width
        // on every column causes the layout to "behave unexpectedly" - exactly the
        // header/body drift this codebase hit. Any .withWidth() call implies that
        // precondition isn't guaranteed (widths may be set on only some columns),
        // so force autoWidth on whenever at least one column declares a width.
        if (!this._config.autoWidth && this._config.columns.some(c => c.width)) {
            this._config.autoWidth = true;
        }

        // Auto-enable multi-row selection when a checkbox column is present
        const hasCheckboxCol = this._config.columns.some(c => c._isCheckbox);
        if (this._config.serverSide && this._config.columns.some(c => c._sortKey)) {
            console.warn('DataTableBuilder: withSortKey only applies to client-side ordering; implement ordering on the server.');
        }
        const selectionMode = this._selectionMode || (hasCheckboxCol ? Constants.Selection.MULTI : Constants.Selection.NONE);

        if (hasCheckboxCol && !this._config.select) {
            if ($.fn.DataTable && $.fn.DataTable.select) {
                this._config.select = {
                    style: Constants.Selection.MULTI,
                    selector: 'td.select-checkbox:not(.pe-none)'
                };
            } else {
                this._config.select = false; // Adapter handles it manually
            }
        }

        // Checkbox columns are sortable by default (by checked state) unless
        // .notSortable() was called. Two order concerns remain:
        //  1. Non-sortable checkbox columns are orderable:false, but DataTables
        //     still paints sorting_asc/desc when `order` targets them — strip them.
        //  2. Default order is [[0,'asc']], so a leading checkbox would make the
        //     table open sorted by tick state. Only honour that when the caller
        //     set an explicit order via withOptions({ order }).
        if (hasCheckboxCol) {
            const lockedIndexes = new Set(
                this._config.columns
                    .map((c, i) => (c._isCheckbox && !c._checkboxSortable ? i : -1))
                    .filter((i) => i >= 0)
            );
            const sortableCheckboxIndexes = new Set(
                this._config.columns
                    .map((c, i) => (c._isCheckbox && c._checkboxSortable ? i : -1))
                    .filter((i) => i >= 0)
            );
            let order = Array.isArray(this._config.order) ? this._config.order.slice() : [];
            order = order.filter((o) => Array.isArray(o) && !lockedIndexes.has(o[0]));

            // The untouched default order points at column 0; if that is a
            // sortable checkbox, it was not really requested — open sorted by
            // the first data column instead.
            const implicitCheckboxDefault = !this._explicitOrder
                && order.length > 0
                && sortableCheckboxIndexes.has(order[0][0]);

            if (order.length === 0 || implicitCheckboxDefault) {
                const firstOrderable = this._config.columns.findIndex(
                    (c) => c.orderable !== false && !c._isCheckbox
                );
                order = firstOrderable >= 0 ? [[firstOrderable, 'asc']] : [];
            }
            this._config.order = order;
        }

        // Guard: strip `buttons` config if the Buttons extension isn't loaded
        if (this._config.buttons && this._config.buttons.length > 0) {
            const buttonsLoaded = $.fn.DataTable && (
                typeof $.fn.DataTable.Buttons !== 'undefined' ||
                ($.fn.DataTable.ext && $.fn.DataTable.ext.buttons)
            );
            if (!buttonsLoaded) {
                this._config.buttons = [];
            }
        }

        // Mark the raw table before DataTables rewrites the DOM
        $el.addClass('dt-core');

        // ── Premium Feature: Automatic Filter Plucking ──────────────────
        attachFilterPlucking(this._config, this._filters);


        this._config.dom = this._buildDom();
        const userDrawCallback = this._config.drawCallback;
        const userInitComplete = this._config.initComplete;
        // AJAX tables: drive onComplete from the xhr event so reloads fire it too.
        // Local tables: use initComplete (with pending replay — it runs before the adapter exists).
        const willUseAjax = !!this._config.ajax;
        let pendingInitComplete = null;
        if (userDrawCallback) {
            this._config.drawCallback = (settings) => {
                if (this._adapter) {
                    userDrawCallback.call(this._adapter, settings, this._adapter);
                }
            };
        }
        if (userInitComplete && !willUseAjax) {
            this._config.initComplete = (settings, json) => {
                if (this._adapter) {
                    userInitComplete.call(this._adapter, settings, json, this._adapter);
                } else {
                    pendingInitComplete = { settings, json };
                }
            };
        } else if (userInitComplete && willUseAjax) {
            // Avoid double-firing: first ajax load triggers both xhr and initComplete.
            delete this._config.initComplete;
        }
        // ── Deferred Load Support ──────────────────────────────────────
        // Save AJAX config before stripping it so the table initializes without
        // making any request. The adapter's loadData() restores and fires it.
        let deferredAjax = null;
        if (this._deferLoad && this._config.ajax) {
            deferredAjax = this._config.ajax;
            this._config.ajax = null;
        }

        if ($.fn.DataTable.isDataTable($el)) {
            const existingNode = $el.get(0);
            const existingAdapter = _adapterRegistry.get(existingNode);
            if (existingAdapter) {
                existingAdapter.destroy();
            } else {
                $el.off('click.dtWrapperActions');
                $el.DataTable().destroy();
            }
        }

        const dtInstance = $el.DataTable(this._config);

        // Ensure processing indicator is dismissed and Loading... text is cleared when DataTables encounters an error
        $el.on('error.dt', (e, settings, techNote, message) => {
            e.preventDefault();
            const $wrapper = $el.closest('.dataTables_wrapper').length > 0
                ? $el.closest('.dataTables_wrapper')
                : $(dtInstance.table().container());
            $wrapper.find('.dataTables_processing').hide();
            if (dtInstance && typeof dtInstance.processing === 'function') {
                try { dtInstance.processing(false); } catch (_) {}
            }
            if (dtInstance && typeof dtInstance.clear === 'function') {
                try { dtInstance.clear().draw(false); } catch (_) {}
            }
            const $emptyCell = $el.find('tbody td.dataTables_empty');
            if ($emptyCell.length > 0 && ($emptyCell.text().trim() === 'Loading...' || $emptyCell.text().trim() === '')) {
                $emptyCell.text('No data available in table');
            }
        });

        // Apply dynamic conflict fixes for Responsive, FixedColumns, and Zoom/Resize
        this._applyFixedWidthFixes(dtInstance, $el);

        // Alignment Fix: If scrolling is enabled, force a column adjustment after a short delay
        if (this._config.scrollY) {
            setTimeout(() => {
                dtInstance.columns.adjust();
            }, 50);
        }

        // Find the true DataTables wrapper to apply core styling
        const $dtWrapper = $el.closest('.dataTables_wrapper').length > 0
            ? $el.closest('.dataTables_wrapper')
            : $(dtInstance.table().container());

        $dtWrapper.addClass('dt-core-wrapper');
        
        if (this._config.theme) {
            $dtWrapper.addClass(`dt-theme-${this._config.theme}`);
        }

        if (this._config.cardLayout) {
            $dtWrapper.addClass('dt-card-layout');
        }

        if (this._config.compactPaging) {
            $dtWrapper.addClass('dt-compact-paging');
        }

        if (this._config.denseRows) {
            $dtWrapper.addClass('dt-dense-rows');
        }

        // Force type="search" for better UX (native clear button)
        $dtWrapper.find('.dataTables_filter input, .dt-search input').attr('type', 'search');

        if (hasHeaderGroups) {
            const syncGroupSortClasses = () => {
                if (!dtInstance || typeof dtInstance.order !== 'function') return;
                const currentOrder = dtInstance.order();
                const activeColIdx = (currentOrder && currentOrder.length > 0) ? currentOrder[0][0] : -1;
                const activeDir = (currentOrder && currentOrder.length > 0) ? currentOrder[0][1] : 'asc';

                $dtWrapper.find('thead tr:first-child th.dt-group-header-sortable').each(function () {
                    const $th = $(this);
                    const sortCol = parseInt($th.attr('data-dt-sort-col'), 10);
                    $th.removeClass('sorting_asc sorting_desc').addClass('sorting');
                    if (sortCol === activeColIdx) {
                        $th.removeClass('sorting').addClass(`sorting_${activeDir}`);
                    }
                });
            };

            $dtWrapper.off('click.dtGroupSort').on('click.dtGroupSort', 'thead tr:first-child th.dt-group-header-sortable', function (e) {
                e.preventDefault();
                e.stopPropagation();
                const $th = $(this);
                const sortCol = parseInt($th.attr('data-dt-sort-col'), 10);
                if (isNaN(sortCol) || !dtInstance || typeof dtInstance.order !== 'function') return;

                const currentOrder = dtInstance.order();
                let newDir = 'asc';
                if (currentOrder && currentOrder.length > 0 && currentOrder[0][0] === sortCol) {
                    newDir = currentOrder[0][1] === 'asc' ? 'desc' : 'asc';
                }
                const result = dtInstance.order([[sortCol, newDir]]);
                if (result && typeof result.draw === 'function') {
                    result.draw();
                }
                syncGroupSortClasses();
            });

            if (dtInstance && typeof dtInstance.on === 'function') {
                dtInstance.on('draw.dtGroupSort', syncGroupSortClasses);
            }
            syncGroupSortClasses();
        }

        this._adapter = new DataTableAdapter(dtInstance, this._config, this._actionCallbacks, {
            selectionMode,
            hasCheckboxCol,
            jumpToPage: this._jumpToPage,
            selectionCallbacks: this._selectionCallbacks,
            deferredAjax
        });

        if (this._config.summary) {
            this._adapter._initSummary();
        }

        // Manually trigger draw callback for the initial state since the native 
        // one might have been skipped during sync initialization
        if (userDrawCallback) {
            userDrawCallback.call(this._adapter, dtInstance.settings()[0], this._adapter);
        }

        // Replay initComplete that fired before the adapter was ready (sync/local data)
        if (userInitComplete && pendingInitComplete) {
            userInitComplete.call(
                this._adapter,
                pendingInitComplete.settings,
                pendingInitComplete.json,
                this._adapter
            );
        }

        // AJAX: fire onComplete after every successful data load (initial + reload)
        if (userInitComplete && willUseAjax) {
            const adapter = this._adapter;
            dtInstance.on('xhr.dt.dtWrapperOnComplete', (e, settings, json) => {
                userInitComplete.call(adapter, settings, json, adapter);
            });
        }

        // Initialize core column filters if any are defined
        if (this._config.columns.some(c => c.filterType)) {
            this._adapter._initColumnFilters();
        }

        // Initialize toolbar buttons if defined
        if (this._config.toolbarButtons && this._config.toolbarButtons.length > 0) {
            this._adapter._initToolbar();
        }

        // ── Premium Feature: Automatic Search Wiring ────────────────────
        if (this._searchTriggers.length > 0) {
            const adapter = this._adapter;
            const tableId = $el.attr('id') || `dt-${Math.random().toString(36).substr(2, 9)}`;
            const eventNamespace = `.dt-trigger-${tableId}`;
            
            this._searchTriggers.forEach(trigger => {
                const namespacedEvents = trigger.events.split(' ')
                    .map(e => `${e}${eventNamespace}`)
                    .join(' ');

                // Debounce input-heavy events (input, keyup)
                const isHighFreq = trigger.events.includes('input') || trigger.events.includes('keyup');
                // loadData restores deferred ajax on first trigger; otherwise reloads.
                const reloadFn = isHighFreq ? debounce(() => adapter.loadData(), 300) : () => adapter.loadData();

                $(document).on(namespacedEvents, trigger.selector, (e) => {
                    const isInput = $(e.target).is('input, select, textarea');
                    // Prevent default for clicks on non-input elements (buttons, links)
                    if (!isInput && e.type === 'click') {
                        e.preventDefault();
                    }
                    
                    reloadFn();
                });
            });

            // Pass namespace to adapter for cleanup on destroy
            this._adapter._triggerNamespace = eventNamespace;
        }

        return this._adapter;
    }
}

// Expose Constants on the Builder
Object.assign(DataTableBuilder, Constants);

